import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Button, Card, CardContent, Grid, Typography, Link, DialogContentText, Switch, Box, Checkbox } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { deleteIcon, greenCheckIcon, runmultiple, downloadIcon, runmultiples, digital, redCrossIcon } from '../assets';
import { DataGrid, Modal } from '../ui';
import { IGridcolumn } from '../ui/interface/column.interface';
import { GlobalLibrary } from './globalLibrary';
import { ProjectLibrary } from './projectLibrary';
import { NewTestCase } from './newTestCase';
import { SegmentEditor } from './segmentEditor';
import { useSelector } from 'react-redux';
import { upsertVariable as upsertLibraryVariable } from '../store/resuders/variable.reducer';
import { upsertGrammar as upsertLibraryGrammar } from '../store/resuders/grammar.reducer';
import { upsertPrompt as upsertLibraryPrompt } from '../store/resuders/prompt.reducer';
import { upsertAudio as upsertLibraryAudio } from '../store/resuders/audio.reducer';
import InfoIcon from '@mui/icons-material/Info';
import PendingOutlinedIcon from '@mui/icons-material/PendingOutlined';
import { getCurrentProject, getCurrentTestCase, selectBrowsers } from '../store/selectors/project.selector';
import PhoneEnabledOutlinedIcon from '@mui/icons-material/PhoneEnabledOutlined';
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { allowUpsertLibrary } from '../store/selectors/segment.selector';
import { allowUpsertVariable } from '../store/selectors/variable.selector';
import { allowUpsertGrammar } from '../store/selectors/grammar.selector';
import { allowUpsertPrompt } from '../store/selectors/prompt.selector';
import { allowUpsertAudio } from '../store/selectors/audio.selector';
import { useAppDispatch } from '../store/hooks';
import { hideSegmentLibrary, setBatchSegments, setEditSegment, upsertLibrary as upsertSegmentLibrary } from '../store/resuders/segment.reducer';
import { getTestCaseList, deleteTestCases, getTestCaseById, checkMultiVariable, runMultipleVariableTestCase, runDigitalTestCases } from "../services/testcase.service";
import { ITestCase } from '../types/interfaces/testCase.interface';
import { setCurrentProjectId, setCurrentTestCase, setCurrentProject } from '../store/resuders/project.reducer';
import { LibraryEditor } from '../ui/libraryEditor';
import { getProjectById } from '../services/project.service';
import { getGrammarOptions } from '../services/grammar.service';
import { serGrammaroptions, showHelpDetails, toggleAlert, setFolderTreeCollapsed } from '../store/resuders/app.reducer';
import { getFolderTreeCollapsed } from '../store/selectors/app.selector';
import { formatTimestamp } from '../helpers/date.helper';
import { useTranslation } from 'react-i18next';
import { SearchInput } from '../ui';
import { getHelpCenter } from '../store/selectors/app.selector';
import { uniqueId, closeLibraryEditor } from '../helpers/form.helper';
import { CHANNELS } from '../types/global.enum';
import { DigitalSegmentEditor } from './digital/digitalSegmentEditor';
import InsertCommentIcon from '@mui/icons-material/InsertComment';
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';

export const TestCaseManagement = () => {
    // Get both projectId and testCaseId from URL. testCaseId will be undefined if not present.
    const { projectId, testCaseId } = useParams<{ projectId: string; testCaseId?: string }>();
    const prevTestCaseIdRef = useRef<string | undefined>(); // Ref to store previous testCaseId
    const currentTestCase = useSelector(getCurrentTestCase);
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const project = useSelector(getCurrentProject);
    
    // Remove caseEditorOpen state, as URL param testCaseId will control editor visibility
    // const [caseEditorOpen, setCaseEditorOpen] = useState(false);
    
    // Keep projectLibraryOpen state for now, might be needed for editor
    const [projectLibraryOpen, setProjectLibraryOpen] = useState(false);
    const [globalLibraryOpen, setGlobalLibraryOpen] = useState(false);
    const [edit, setEdit] = useState<any>(null);
    const [runParameters, setRunParameters] = useState<any[]>([]);
    const [browsersToRun, setBrowsersToRun] = useState<string[]>([]);
    const [showSegmentEditor, setShowSegmentEditor] = useState(false); // Add the showSegmentEditor state
    const upsertLibrary = useSelector(allowUpsertLibrary);
    const upsertVariable = useSelector(allowUpsertVariable);
    const upsertGrammar = useSelector(allowUpsertGrammar);
    const upsertPrompt = useSelector(allowUpsertPrompt);
    const upsertAudio = useSelector(allowUpsertAudio);
    const browsers = useSelector(selectBrowsers);
    const dispatch = useAppDispatch();
    const [segmentAddTestCaseEditor, setSegmentAddTestCaseEditor] = useState<boolean>(false);
    // Newly Added
    const [editorOpen, setEditorOpen] = useState(false)  // Track Library Creator/Editor State
    const [editorState, setEditorState] = useState("")
    const [selectedData, setSelectedData] = useState<any[]>([]);  // Contains the data of the clicked library item (edit icon)
    //
    const history = useNavigate();
    const helpCenter = useSelector(getHelpCenter);
    const isTreeCollapsed = useSelector(getFolderTreeCollapsed);
    const [autoCollapsedByEditor, setAutoCollapsedByEditor] = useState(false); // Track if we auto-collapsed

    const [openDiscardConfirmation, setOpenDiscardConfirmation] = useState('');
    
    const [data, setData] = useState<any>([]);
    const [remove, setRemove] = useState<any>([]);
    const [isOpen, setIsOpen] = useState<string>('');
    const [isRunOpen, setIsRunOpen] = useState<string>('');
    const [openSelectBrowser, setOpenSelectBrowser] = useState<string>('');
    const [criteria, setCriteria] = useState<any>({
        sort: '-lastModifiedTime',
        pageNo: 0,
        pageSize: 10,
        filter: '',
        projectId: projectId
    });

    const [discardChangeConfirmation, setDiscardChangeConfirmation] = useState<any>({
        callAfter: () => {},
        onConfrimAction: ()=> {},
        onDiscardAction: ()=> {}
    });

    const pageSizeChanged = (size: number) => {
        setCriteria({
            ...criteria,
            pageSize: size,
            pageNo: 0,
        })
    }

    const runDigital = async ()=> {
        if (browsersToRun.length > 0) {
             await runDigitalTestCases(project?.testProjectID  || projectId || 1, runParameters, browsersToRun.join(','));
            setOpenSelectBrowser('');
        }
    }
    
    const browserSelected = (e:any)=> {
        if(e.target.checked) {
            setBrowsersToRun(browsersToRun.concat(e.target.value))
        } else {
            setBrowsersToRun(browsersToRun.filter(b=> b!== e.target.value))
        }
    }
    // const columns: any[] = [
    //     {
    //         caption: t('Sts'), width: '51px',
    //         redner: (params: any) => (
    //             <div className="flex-center">
    //                 <img src={greenCheckIcon} alt="" />
    //             </div>
    //         ),
    //     },
    //     { caption: t('ID'), width: '55px', fieldName: 'testCaseId', sortable: true, key: true },
    //     {
    //         fieldName: 'name', caption: t('Test Case Name'),
    //         redner: (params: any) => <div className="flex-center">
    //             <Link onClick={gotoTestCase(params)} className="a-link-black">{params.name}</Link></div>
    //     },
    //     { fieldName: 'ivrPhoneNumber', caption: t('Tel No.') },
    //     { fieldName: 'lastModifiedTime', caption: t('Modified'), redner: (params: any) => formatTimestamp(params.lastModifiedTime) },
    // ];
    

    const columns = useMemo(()=> {
        // Check if any test cases have comments
        const hasComments = data?.content?.some((item: any) => item.comment);
        
        const baseColumns: IGridcolumn[] = [
            {
                fieldName: 'testCaseId', caption: '', sortable: false, width: '1px', key: true,
                redner: () => <div style={{ width: '1px', height: '1px', overflow: 'hidden' }}></div>
            }
        ];
        
        // Only add comment column if there are comments
        if (hasComments) {
            baseColumns.push({
                fieldName: 'comment',
                caption: '',
                width: '40px',
                redner: (props: any) =>
                    <div>
                    {props.comment && (
                     <Tooltip title={props.comment}  arrow>
                        <InsertCommentIcon style={{ fontSize: '20px', color: 'black' , margin: "auto" }} />
                    </Tooltip>
                    )}
                    </div>
            });
        }
        
        // Add remaining columns
        baseColumns.push(
            {
                fieldName: 'name', caption: t('Test Case Name'), sortable: true, width: 'auto',
                redner: (props: any) => <div className="flex-center" style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                    <Link onClick={gotoTestCase(props)} className="a-link-black">{props.name}</Link>
                </div>
            },
            project?.data?.channelType === CHANNELS.DIGITAL ? {
                fieldName: 'ivrPhoneNumber', caption: '', sortable: true, width: 'auto',
                redner: (props: any) => <div className="flex-center"></div>
            }:{
                fieldName: 'ivrPhoneNumber', caption: t('Tel No.'), sortable: true, width: 'auto',
                redner: (props: any) => <div className="flex-center" style={{ whiteSpace: 'nowrap' }}>
                    {props.ivrPhoneNumber}
                </div>
            },
            { 
                fieldName: 'lastModifiedTime', 
                caption: t('Modified'), 
                sortable: true, 
                width: 'auto',
                redner: (props: any) => <div style={{ whiteSpace: 'nowrap' }}>{formatTimestamp(props.lastModifiedTime)}</div>
            }
        );
        
        return baseColumns as any;
    },[project, t, data])
    
    const gotoTestCase = (testcase: ITestCase) => () => {
        if (!testcase.testCaseId || !projectId) return;
        
        // Determine base path based on current location
        const isFoldersView = location.pathname.startsWith('/folders/');
        const basePath = isFoldersView ? `/folders/project/${projectId}` : `/projects/${projectId}`;
        
        console.log(`Navigating to test case: ${testcase.testCaseId} from base: ${basePath}`);
        navigate(`${basePath}/testcase/${testcase.testCaseId}`);
    }

    // --- Function to load ONLY the test case list based on criteria ---
    const loadTestCaseListData = async () => {
        if (!criteria.projectId) {
            console.warn('loadTestCaseListData called without criteria.projectId');
            return;
        }
        // Need project channel type, get it from Redux state (assuming loadProjectData ran first)
        const currentProjectType = project?.data?.channelType;
        if (!currentProjectType) {
            console.warn('loadTestCaseListData called before project data was loaded');
            // Optionally, wait for project data or handle gracefully
            return; 
        }
        console.log("Loading test case list data for criteria:", criteria);
        const { data: testCaseData } = await getTestCaseList(
            criteria.projectId, 
            currentProjectType, 
            criteria.pageNo, 
            criteria.pageSize, 
            criteria.sort, 
            criteria.filter
        );
        setData(testCaseData);
    }

    // --- Function to load core project data (details, grammars) ---
    const loadProjectData = useCallback(async (currentProjectId: string | number) => {
        if (!currentProjectId) {
            console.warn('loadProjectData called without currentProjectId');
            return;
        }
        console.log(`Loading core project data for project ID: ${currentProjectId}`);
        try {
            const numericProjectId = Number(currentProjectId);
            const projectData = await getProjectById(numericProjectId);
            const projectGrammar = await getGrammarOptions(numericProjectId);

            dispatch(serGrammaroptions(projectGrammar));
            dispatch(setCurrentProject(projectData)); 
            dispatch(setCurrentProjectId(String(currentProjectId))); 
            return projectData?.data?.channelType; // Return channel type for list loading
        } catch (error) {
            console.error(`Failed to load core data for project ${currentProjectId}:`, error);
            // Handle error appropriately, maybe clear project state
            dispatch(setCurrentProject(null));
            dispatch(setCurrentProjectId(null));
            dispatch(serGrammaroptions([]));
            return null;
        }
    }, [dispatch]); // Dependencies: dispatch

    const editProject = (id: string) => {
        // Assuming data is the test case list, find might not work for project edit
        // Re-evaluate if this edit function is still needed or how it should work
        // setEdit(data.find((d: any) => +d.id === +id)); 
        // setProjectLibraryOpen(true);
        console.warn('editProject function needs review');
    }

    const toggleCaseEditor = () => {
        if (!projectLibraryOpen) {
            dispatch(setCurrentTestCase(null));
            dispatch(setBatchSegments([]));
        }

        setProjectLibraryOpen(!projectLibraryOpen);
        setEdit(null);
        setShowSegmentEditor(false)
        setGlobalLibraryOpen(false);
    };

    const toggleGlobalLibrary = () => {
        setGlobalLibraryOpen(!globalLibraryOpen);
        setProjectLibraryOpen(false);
    };

    const toggleProjectLibrary = () => {
        setProjectLibraryOpen(!projectLibraryOpen);
        setGlobalLibraryOpen(false);
        
        // Close all editors when closing the library
        if (projectLibraryOpen) {
            setEditorOpen(false);
            setShowSegmentEditor(false);
            setSelectedData([]);
            dispatch(setEditSegment(null));
            closeLibraryEditor(dispatch);
        }
    };

    const toggleSegmentEditor = (close?: boolean, open?: boolean) => {
        if (open) {
            setShowSegmentEditor(true);
        } else if (close) {
            setShowSegmentEditor(false);
            dispatch(hideSegmentLibrary());
        } else {
            setShowSegmentEditor(!showSegmentEditor); // Toggle the showSegmentEditor state
        }
    };

    const onFilterChanged = (filter: string) => {
        setCriteria({
            ...criteria,
            pageNo: 0,
            filter,
        })
    }

    const sortChanged = (column: string, direction: string) => {
        setCriteria({
            ...criteria,
            pageNo: 0,
            sort: `${direction === 'ASC' ? '+' : '-'}${column}`,
        })
    }

    const pageChanged = (page: number) => {
        setCriteria({
            ...criteria,
            pageNo: page - 1,
        })
    }

    const selectionChanged = (id: any) => {
        setRemove(id);
    }

    const deleteSelected = async () => {
        if (remove?.length) {
            setIsOpen(`${new Date().getTime()}`);
        }
    }

    const deleteTestCase = async () => {
        const result: any =  await deleteTestCases(project.testProjectID, remove,project?.data?.channelType);
        if (result?.some((r:any)=>["ERR_BAD_RESPONSE", "ERR_BAD_REQUEST"].includes(r?.code))) {
            dispatch(toggleAlert(result.filter((r:any)=>["ERR_BAD_RESPONSE", "ERR_BAD_REQUEST"].includes(r.code))?.[0]?.response?.data?.data?.messages?.join(',')))
        } else {
            dispatch(toggleAlert(t('Test Case Deleted Successfully')));
        }
        await loadTestCaseListData();
        setRemove([]);
    }

    // --- Sync criteria with projectId from URL ---
    useEffect(() => {
        // console.log("Project ID from URL changed:", projectId);
        if (projectId && projectId !== criteria.projectId) { // Update only if different
            setCriteria((currentCriteria: any) => ({
                ...currentCriteria,
                projectId: projectId,
                pageNo: 0,
            }));
            setData([]); 
            // Clearing currentTestCase is handled by the testCaseId effect below
        } 
    }, [projectId, criteria.projectId, dispatch]);

    // --- Load core project data whenever projectId changes --- 
    useEffect(() => {
      if (projectId) {
        loadProjectData(projectId);
      }
    }, [projectId, loadProjectData]);

    // --- Load test case LIST data when criteria changes (but only if not viewing a specific case) ---
    useEffect(() => {
        // Only load list if projectId is set and matches criteria, and no specific test case is open
        if (criteria.projectId && criteria.projectId === projectId && !testCaseId) { 
          console.log("Criteria changed for list view, calling loadTestCaseListData:", criteria);
          loadTestCaseListData(); // Use the dedicated list loading function
        }
    }, [criteria, testCaseId, projectId]) // Depends on criteria, testCaseId, projectId


    useEffect(() => {
      if (project?.data?.channelType && criteria.projectId === projectId && !testCaseId) {
        loadTestCaseListData();
      }
    }, [project?.data?.channelType]); // Load list once channelType arrives

    // --- Load specific test case data OR handle 'new' when URL changes ---
    useEffect(() => {
        const loadSpecificTestCase = async () => {
            if (projectId && testCaseId) {
                 // Open the project library whenever viewing/creating a specific test case
                 setProjectLibraryOpen(true); 

                 // Handle the 'new' case for creating a test case
                 if (testCaseId === 'new') {
                    console.log(`Creating new test case in project ${projectId}`);
                    dispatch(setCurrentTestCase(null)); // Clear current test case for creation
                    dispatch(setBatchSegments([])); // Clear segments from previous case
                    setEdit(null);
                    setShowSegmentEditor(false);
                    // Allow user to open library if they want
                    // setProjectLibraryOpen(false); 
                    // setGlobalLibraryOpen(false); 
                    // No API call needed, just render NewTestCase in create mode
                } else {
                    // Existing logic to load a specific test case by numeric ID
                    console.log(`Attempting to load test case ID: ${testCaseId} in project ${projectId}`);
                    try {
                        // Attempt to parse ID to ensure it's numeric before fetching
                        const numericTestCaseId = parseInt(testCaseId, 10);
                        if (isNaN(numericTestCaseId)) {
                            console.warn(`Invalid numeric testCaseId: ${testCaseId}. Navigating back.`);
                            dispatch(toggleAlert(`Invalid test case ID: ${testCaseId}.`));
                            navigate(`/folders/project/${projectId}`, { replace: true });
                            return;
                        }

                        const { data: testCaseData } = await getTestCaseById(
                            Number(projectId),
                            numericTestCaseId, // Use parsed numeric ID
                            project?.data?.channelType 
                        )(dispatch);
                        
                        if (testCaseData) {
                            dispatch(setCurrentTestCase(testCaseData));
                            setEdit(null); // Ensure edit state is cleared
                            setShowSegmentEditor(false); // Ensure segment editor is closed initially
                        } else {
                            console.warn(`Test case with ID ${testCaseId} not found in project ${projectId}. Navigating back.`);
                            dispatch(toggleAlert(`Test case ${testCaseId} not found.`));
                            navigate(`/folders/project/${projectId}`, { replace: true }); // Navigate back to project view
                        }
                    } catch (error) {
                        console.error(`Failed to fetch test case ${testCaseId}:`, error);
                        dispatch(toggleAlert(`Failed to load test case ${testCaseId}.`));
                        navigate(`/folders/project/${projectId}`, { replace: true }); // Navigate back on error
                    }
                }
            } else {
                 // If testCaseId is not present in the URL (viewing the list)
                dispatch(setCurrentTestCase(null));
                dispatch(setBatchSegments([])); // Clear segments when leaving a case view
                // Close the project library ONLY if we transitioned from a view WITH a test case ID
                if (prevTestCaseIdRef.current && projectLibraryOpen) {
                    console.log('Navigated away from test case, closing library.');
                    setProjectLibraryOpen(false);
                }
            }
        };
        loadSpecificTestCase();

        // Update the ref *after* the logic has run for the current render
        prevTestCaseIdRef.current = testCaseId; 

    // Depend on projectId, testCaseId from URL, dispatch, navigate, project?.data?.channelType, and projectLibraryOpen
    // Remove projectLibraryOpen dependency as library state shouldn't trigger test case loading
    }, [projectId, testCaseId, dispatch, navigate, project?.data?.channelType]);

    let gridItemSize = 12;
    const isAnyLibraryOpen = globalLibraryOpen || projectLibraryOpen;
    const isAnyEditorOpen = showSegmentEditor || upsertLibrary || upsertVariable || upsertGrammar || upsertPrompt || upsertAudio || editorOpen;

    // Auto-collapse folder tree when editor opens while test case editor is open
    useEffect(() => {
        if (testCaseId && isAnyEditorOpen && !isTreeCollapsed && !autoCollapsedByEditor) {
            dispatch(setFolderTreeCollapsed(true));
            setAutoCollapsedByEditor(true);
        }
        // Reset flag when editor closes
        if (!isAnyEditorOpen && autoCollapsedByEditor) {
            setAutoCollapsedByEditor(false);
        }
    }, [testCaseId, isAnyEditorOpen, isTreeCollapsed, autoCollapsedByEditor, dispatch]);
    
    if (testCaseId) { // If viewing a specific test case (editor is open)
        if (isAnyEditorOpen) {
            gridItemSize = 4; // Make space for Variable/Grammar/etc. editors
        } else if (isAnyLibraryOpen || showSegmentEditor) { // Original logic used caseEditorOpen here
             gridItemSize = isAnyLibraryOpen && showSegmentEditor ? 4 : 6; // Editor + Library + SegmentEditor = 4, Editor + (Library OR SegEditor) = 6
        } else {
             gridItemSize = 12; // Editor takes full width if nothing else is open
        } 
    } else { // If viewing the test case list
        if (isAnyEditorOpen) {
            gridItemSize = 6; // Test case list hidden, Library + Editor = 2 columns (50/50)
        } else if (isAnyLibraryOpen) {
            gridItemSize = 6; // Test case list + Library = 2 columns (50/50)
        } else {
            gridItemSize = 12; // Test case list only = full width
        }
    }

    const getTabType = () => {
        if (upsertVariable) {
            return "Variable"
        } else if (upsertGrammar) {
            return "Grammar"
        } else if (upsertPrompt) {
            return "Prompt"
        } else if (upsertAudio) {
            return "Audio"
        }
    }

    const goToTestCases = async () => {
        // Keep state cleanup
        setProjectLibraryOpen(false);
        dispatch(setCurrentTestCase(null));
        dispatch(setEditSegment(null));
        dispatch(upsertSegmentLibrary(''))
        dispatch(upsertLibraryAudio(''))
        dispatch(upsertLibraryGrammar(''))
        dispatch(upsertLibraryPrompt(''))
        dispatch(upsertLibraryVariable(''))

        // Add navigation logic
        if (!projectId) return; // Need projectId to navigate

        const isFoldersView = location.pathname.startsWith('/folders/');
        const backPath = isFoldersView ? `/folders/project/${projectId}` : `/projects/${projectId}/testcases`; // Use correct non-folder path
        
        console.log(`goToTestCases navigating back to: ${backPath}`);
        navigate(backPath);
    }

    const checkIsAnyEditorOpen  = () => showSegmentEditor || editorOpen;

    const text = (<>
        <DialogContentText sx={{
                        fontSize: "16px",
                        color: '#000000',
                        textAlign: "center",
                        fontFamily: 'Roboto',
                        mt: 5,
                        mb: 5,
                    }}>
                    <div>
                        <ErrorOutlineIcon style={{ fontSize: '50px', color: '#FD5900', paddingBottom: '33px' }} />
                    </div>
                    {`Do you want to save the changes to the ${getTabType()} Library?`}
        </DialogContentText>
    </>);

    const checkMultiRun = async()=> {
        const runCheck = await checkMultiVariable(project.testProjectID, remove.join(','), project?.data?.channelType);
        setRunParameters(runCheck);
        setIsRunOpen(uniqueId());
    }

    const setItCount = (testCase:any)=>(e:any)=> {
        setRunParameters(
            runParameters.reduce((res:any[],curr:any)=> {
                if(curr.testCaseId === testCase.testCaseId) {
                    res.push({
                        ...curr,
                        runAll:e.target.checked,
                    })
                } else {
                    res.push(curr);
                }
                return res;
            }, [])
        )
    }

    const runMultiple = async()=> {
        await runMultipleVariableTestCase(project.testProjectID, runParameters);
        setIsRunOpen('');
    }

    const handleDiscardChangeConfirmation = async () => {
        await discardChangeConfirmation?.onConfrimAction();
        discardChangeConfirmation.callAfter();
    }

    // --- Update handleCloseEditor to navigate back based on current path ---
    const handleCloseEditor = () => {
      if (!projectId) return; 

      // Close all editors before navigating
      setEditorOpen(false);
      setShowSegmentEditor(false);
      setSelectedData([]);
      dispatch(setEditSegment(null));
      closeLibraryEditor(dispatch);

      const isFoldersView = location.pathname.startsWith('/folders/');
      // Navigate back to the appropriate list view
      const backPath = isFoldersView ? `/folders/project/${projectId}` : `/projects/${projectId}/testcases`;
      
      console.log(`Closing editor, navigating back to: ${backPath}`);
      navigate(backPath);
    };

    return (
        <DndProvider backend={HTML5Backend}>
            <Grid 
              container 
              sx={{ 
                height: '100%', // Fill parent height
                overflow: 'auto', // Handle internal overflow
                display: 'flex',  // Use flex for children
              }}
            >
                {/* Breadcrumb Header Section - Only show when NOT in test case editor */}
                {!testCaseId && (
                    <Grid item xs={6} sx={{ flexShrink: 0 }}>
                        <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '8px', 
                            height: '100%',
                            overflow: 'hidden',
                            minWidth: 0
                        }}>
                            <Typography variant="subtitle2" sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                                {t('Project')}:
                            </Typography>
                            <Typography variant="subtitle2" sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                                {project ? project.testProjectID : projectId}
                            </Typography>
                            <div style={{ flexShrink: 0 }}>
                                {project?.data?.channelType === CHANNELS.VOICE ? (
                                    <PhoneEnabledOutlinedIcon style={{ fontSize: '17px' }} />
                                ) : (
                                    <img src={digital} alt="" />
                                )}
                            </div>
                            <Typography 
                                onClick={goToTestCases} 
                                className={currentTestCase ? 'underline' : ''} 
                                variant="subtitle2"
                                sx={{ 
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    minWidth: 0,
                                    cursor: 'pointer'
                                }}
                            >
                                {project?.data?.name}
                            </Typography>

                            {currentTestCase && (
                                <>
                                    <Typography variant="subtitle2" sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                                        {'>'}
                                    </Typography>
                                    <Typography variant="subtitle2" sx={{ 
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        minWidth: 0
                                    }}>
                                        {currentTestCase?.name}
                                    </Typography>
                                    {helpCenter && (
                                        <div 
                                            onClick={() => dispatch(showHelpDetails('Test Case Navigation Panel'))} 
                                            className="help-icon-float-right"
                                            style={{ flexShrink: 0 }}
                                        >
                                            <InfoIcon />
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </Grid>
                )}
                
                {/* Header - Action Buttons */}
                <Grid item xs={testCaseId ? 12 : 6} textAlign="right" marginBottom={'16px'}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', alignItems:'center', marginRight: '12px' }}>
                        {/* New Test Case Button / Close Editor Button */}
                        {/* Button text/action depends on whether a testCaseId is in the URL */}
                        <Button
                          variant={testCaseId ? 'outlined' : 'contained'}
                          color="primary"
                          onClick={
                            testCaseId 
                              ? handleCloseEditor 
                              : () => {
                                  if (projectId) {
                                    navigate(`/folders/project/${projectId}/testcase/new`);
                                  } else {
                                    console.error("Cannot navigate to new test case: projectId is missing.");
                                    // Optionally show an error to the user
                                  }
                                }
                          }
                        >
                            {testCaseId ? t('Close Case Editor') : t('New Test Case')}
                        </Button>
                        {/* Project Library Button */}
                        <Button
                            variant={projectLibraryOpen ? 'outlined' : 'contained'}
                            color="primary"
                            onClick={toggleProjectLibrary}
                        >
                            {projectLibraryOpen ? t('Close Library') : t('Project Library')}
                        </Button>
                    </div>
                </Grid>

                {/* Main Content: Case Editor or DataGrid based on URL */}
                {testCaseId ? (
                    // Render Editor View when testCaseId is present
                    <Grid item xs={gridItemSize} paddingRight={'9px'} >
                        {helpCenter && <div onClick={()=>dispatch(showHelpDetails('Test Case Editor/Add Segment'))} className="help-icon-float"><InfoIcon /></div>}
                        {/* Pass necessary props to NewTestCase. It might need projectId and testCaseId */}
                        <NewTestCase
                          key={testCaseId}
                          setSegmentAddTestCaseEditor={setSegmentAddTestCaseEditor}
                          toggleSegmentEditor={toggleSegmentEditor}
                        />
                    </Grid>
                ) : (
                    // Render DataGrid View when testCaseId is not present AND no editor is open
                    !isAnyEditorOpen && (
                        <Grid item xs={gridItemSize} paddingRight={'9px'}>
                            <Card>
                                <CardContent>
                                    <div className='test-result-header'>
                                        <div className="action-header">
                                            {/* <img src={lockIcon} alt="" /> */}
                                            <img src={deleteIcon} alt={t("Remove")} onClick={deleteSelected} />
                                            <img src={downloadIcon} alt=""  />  
                                            <img onClick={checkMultiRun} src={remove?.length>0 ? runmultiples:runmultiple} alt="" />  {helpCenter && <div onClick={()=>dispatch(showHelpDetails('Project View page'))} className="help-icon"><InfoIcon /></div>}
                                        </div>
                                        <div className="test-results-filters">
                                            <SearchInput onFilter={onFilterChanged} />
                                        </div>
                                    </div>

                                    <DataGrid
                                        pageChanged={pageChanged}
                                        totalPages={data?.totalPages || 0}
                                        sortChanged={sortChanged}
                                        columns={columns}
                                        data={data?.content || []}
                                        selection={true}
                                        sortBy={criteria.sort}
                                        selectionChanged={selectionChanged}
                                        pageSizeChanged={pageSizeChanged} />
                                </CardContent>
                            </Card>
                        </Grid>
                    )
                )}
                
                {/* Segment Editor */}
                {(showSegmentEditor && !(upsertLibrary || upsertVariable || upsertGrammar || upsertPrompt || upsertAudio)) && (
                    <Grid item xs={gridItemSize} paddingRight={'9px'}>
                        {showSegmentEditor && <div>{helpCenter && <div id="Segment Editor Help"  onClick={()=>dispatch(showHelpDetails('Segment Editor'))} className="help-icon-float"><InfoIcon /></div>}
                        
                        {project?.data?.channelType === CHANNELS.VOICE ? <SegmentEditor 
                            segmentAddTestCaseEditor={segmentAddTestCaseEditor}
                            setSegmentAddTestCaseEditor={setSegmentAddTestCaseEditor}
                            projectLibrary={false} 
                            toggleSegmentEditor={toggleSegmentEditor} 
                            setDiscardChangeConfirmation={setDiscardChangeConfirmation}/>:<DigitalSegmentEditor  projectLibrary={false} toggleSegmentEditor={toggleSegmentEditor} />}
                            </div>
                        }
                    </Grid>
                )}

                {/* Global/Project Library */}
                {(globalLibraryOpen || projectLibraryOpen) && (
                    <Grid item xs={gridItemSize} paddingRight={'9px'}>
                        {globalLibraryOpen && <GlobalLibrary />}
                        {projectLibraryOpen && 
                            <div> {helpCenter && <div id="Project Library"  onClick={()=>dispatch(showHelpDetails('Project Library'))} className="help-icon-float"><InfoIcon /></div>}
                            <ProjectLibrary
                                toggleSegmentEditor={toggleSegmentEditor}
                                gridItemSize={gridItemSize}
                                editorOpen={editorOpen}
                                setEditorOpen={setEditorOpen}
                                selectedData={selectedData}
                                setSelectedData={setSelectedData}
                                editorState={editorState}
                                setEditorState={setEditorState}
                                caseEditorOpen={!!testCaseId}
                                checkIsAnyEditorOpen={checkIsAnyEditorOpen}
                                discardChangeConfirmation={discardChangeConfirmation}
                                setDiscardChangeConfirmation={setDiscardChangeConfirmation}
                                setOpenDiscardConfirmation={setOpenDiscardConfirmation}
                        /> </div>}
                    </Grid>
                )}

                {upsertLibrary && !editorOpen && (
                    <Grid item xs={gridItemSize} paddingRight={'9px'}>
                        {helpCenter && <div id="Segment Editor Help"  onClick={()=>dispatch(showHelpDetails('Segment Editor'))} className="help-icon-float"><InfoIcon /></div>}
                        
                        {project?.data?.channelType === CHANNELS.VOICE ? <SegmentEditor 
                        segmentAddTestCaseEditor={segmentAddTestCaseEditor}
                        setSegmentAddTestCaseEditor={setSegmentAddTestCaseEditor}
                            projectLibrary={true} 
                            toggleSegmentEditor={toggleSegmentEditor} 
                            setDiscardChangeConfirmation={setDiscardChangeConfirmation} />
                            :<DigitalSegmentEditor  projectLibrary={true} toggleSegmentEditor={toggleSegmentEditor} />}
                    </Grid>
                )}


                {/* Segment Editor */}
                {(upsertVariable || upsertGrammar || upsertPrompt || upsertAudio) && (
                    <Grid item xs={gridItemSize} paddingRight={'9px'}>
                        <LibraryEditor
                            selectedData={selectedData} //
                            setSelectedData={setSelectedData} //
                            editorOpen={true}
                            setEditorOpen={setEditorOpen} //
                            type={getTabType()}
                            project={project}
                            editorState={editorState} //
                            toggleSegmentEditor={toggleSegmentEditor}
                            setDiscardChangeConfirmation={setDiscardChangeConfirmation}
                        />
                    </Grid>
                )}


                <Modal
                    isOpen={isOpen}
                    title="Delete Items"
                    small={true}
                    deleteConfirmation={true}
                    ButtonDelete={deleteTestCase}
                />
                <Modal 
                    isOpen={openDiscardConfirmation}
                    title='Discard the changes'
                    small={true}
                    deleteConfirmation={true}
                    ButtonDelete={handleDiscardChangeConfirmation}
                    onClosed={() => {
                        discardChangeConfirmation?.onDiscardAction();
                        discardChangeConfirmation?.callAfter()
                    }}
                    callOnClosed={false} //to stop calling onClosed function during confirm
                    body={text}
                />

                <Modal 
                  isOpen={openSelectBrowser}
                  title={t("Browser Selections")}
                  small={true}
                  body={
                    <div className='db w-100'>
                        <div className='db'><h5>{t('Available Browsers:')}</h5></div>
                        {browsers?.map((b:any)=>(
                            <div className='flex align-center gap-8 mt-24'>
                               <Checkbox onChange={browserSelected} value={b.browserCode} /> {b.browserName} ({b.browserVersion})
                            </div>
                        ))}

                         <Box className="segment-editor-save-buttons mt-22 flex-end gap-8">
                            <Button
                                variant={'contained'}
                                color={'primary'}
                                className='max-h-30'
                                onClick={()=>setOpenSelectBrowser('')}
                            >
                                {t('Cancel')}
                            </Button>
                            <Button
                                variant={'contained'}
                                color={'primary'}
                                className='max-h-30'
                                onClick={runDigital}
                            >
                                {t('Run')}
                            </Button>
                        </Box>
                    </div>
                  }
                  />

                <Modal
                    isOpen={isRunOpen}
                    title={t("Run Parameter")}
                    small={true}
                    body ={
                        <div className='db w-100 center'>
                            <div className='db border-blue'>
                                <div className='dib w-50 center'><Typography variant="body1" className="black">{t('Text Case Name')}</Typography></div> <div className='dib w-20 center' >{t('Total Runs')}</div> 
                                <div className='dib center  w-30'>{t('Variables')}</div>
                            </div>
                            <div className='db mb-16'>
                                <div className='dib w-50 center'></div> <div className='dib w-20 center' ></div> 
                                <div className='dib center  w-30 '><div className='flex justify-center gap-24 light-silver'><span>{t('DEFAULT')}</span><span> {t('ALL')}</span></div></div>
                            </div>
                            {runParameters.map(r=>(
                                <div className='db mb-8'>
                                    <div className='dib w-50'><Typography variant="body1" className="black">{r?.testCaseName}</Typography></div> 
                                    <div className='dib w-20 center'><Typography variant="body1" className="black">{r?.iterationCount}</Typography></div> 
                                    <div className='dib center w-30'>
                                        <div className='run-param2'><Switch onChange={setItCount(r)} disabled={r?.iterationCount === 1} /></div>
                                    </div>
                                </div>
                            ))}

                            {project?.data?.channelType !== CHANNELS.DIGITAL ? <Box className="segment-editor-save-buttons mt-22">
                                    <Button
                                        variant={'contained'}
                                        color={'primary'}
                                        className='max-h-30'
                                        onClick={()=>setIsRunOpen('')}
                                    >
                                        {t('Back')}
                                    </Button>
                                    <Button
                                        variant={'contained'}
                                        color={'primary'}
                                        className='max-h-30 ml-80'
                                        onClick={runMultiple}
                                    >
                                        {t('Run')}
                                    </Button>
                            </Box>:
                            <div className='db w-100'>
                                <div className='db'><h5>{t('Available Browsers:')}</h5></div>
                                {browsers?.map((b:any)=>(
                                    <div className='flex align-center gap-8 mt-24'>
                                    <Checkbox onChange={browserSelected} value={b.browserCode} /> {b.browserName} ({b.browserVersion})
                                    </div>
                                ))}
        
                                <Box className="segment-editor-save-buttons mt-22 flex-end gap-8">
                                    <Button
                                        variant={'contained'}
                                        color={'primary'}
                                        className='max-h-30'
                                        onClick={()=>{
                                            setOpenSelectBrowser('');
                                            setIsRunOpen('');
                                        }}
                                    >
                                        {t('Cancel')}
                                    </Button>
                                    <Button
                                        variant={'contained'}
                                        color={'primary'}
                                        className='max-h-30'
                                        onClick={runDigital}
                                    >
                                        {t('Run')}
                                    </Button>
                                </Box>
                            </div>
                            }
                        </div>
                    }
                />

            </Grid>
        </DndProvider>

    );
};
